import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { SkeletonTable } from '@/components/common/SkeletonTable'
import { useRoles, useDepartments } from '@/hooks/useWorkforce'
import { RoleWithDetails, Department } from '@/types/workforce'
import { Link } from '@tanstack/react-router'
import {
  Briefcase,
  Plus,
  Search,
  Users,
  DollarSign,
  TrendingUp,
  Building2,
  MoreHorizontal,
  Edit,
  Eye,
  Star,
  Filter,
  RefreshCw
} from 'lucide-react'

function RolesContent() {
  const { user } = useAuth()

  // 🚀 Server State (TanStack Query)
  const {
    roles,
    loading: rolesLoading,
    isFetching: rolesIsFetching,
    error: rolesError
  } = useRoles()

  const {
    departments,
    loading: departmentsLoading,
    isFetching: departmentsIsFetching
  } = useDepartments()

  // 🎯 Local State (React useState for filters)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedLevel, setSelectedLevel] = useState('')

  // Derived state
  const loading = rolesLoading || departmentsLoading
  const isFetching = rolesIsFetching || departmentsIsFetching

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  const getLevelIcon = (level: string) => {
    const levelOrder = ['junior', 'mid', 'senior', 'lead', 'manager', 'director', 'executive']
    const stars = Math.min(levelOrder.indexOf(level) + 1, 5)
    return Array.from({ length: stars }, (_, i) => (
      <Star key={i} className="w-3 h-3 fill-brand-primary text-brand-primary" />
    ))
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'junior':
        return 'bg-blue-500/20 text-blue-400'
      case 'mid':
        return 'bg-green-500/20 text-green-400'
      case 'senior':
        return 'bg-purple-500/20 text-purple-400'
      case 'lead':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'manager':
        return 'bg-orange-500/20 text-orange-400'
      case 'director':
        return 'bg-red-500/20 text-red-400'
      case 'executive':
        return 'bg-brand-primary/20 text-brand-primary'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const filteredRoles = roles.filter(role => {
    let matches = true

    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      const nameMatch = role.name.toLowerCase().includes(search)
      const descriptionMatch = role.description ? role.description.toLowerCase().includes(search) : false
      matches = matches && (nameMatch || descriptionMatch)
    }

    if (selectedDepartment) {
      matches = matches && role.departmentId?.toString() === selectedDepartment
    }

    if (selectedLevel) {
      matches = matches && role.level === selectedLevel
    }

    return matches
  })

  const totalEmployees = roles.reduce((sum, role) => sum + (role.employees?.length || 0), 0)
  const averageSalaryMin = roles.reduce((sum, role) => sum + (role.salary?.min || 0), 0) / roles.length
  const averageSalaryMax = roles.reduce((sum, role) => sum + (role.salary?.max || 0), 0) / roles.length

  const levelCounts = roles.reduce((acc, role) => {
    if (role.level) {
      acc[role.level] = (acc[role.level] || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
              Cargos
            </h1>
            <p className="font-body text-brand-gray">
              Defina cargos, responsabilidades e estruturas hierárquicas
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/workforce/roles/new">
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Novo Cargo
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-3">
          <MetricCard
            title="Total de Cargos"
            value={roles.length}
            icon={Briefcase}
            trend="neutral"
          />
          <MetricCard
            title="Colaboradores"
            value={totalEmployees}
            icon={Users}
            trend="neutral"
          />
          <MetricCard
            title="Faixa Salarial Média"
            value={`${formatCurrency(averageSalaryMin)} - ${formatCurrency(averageSalaryMax)}`}
            icon={DollarSign}
            trend="neutral"
          />
        </div>

        {/* Level Distribution */}
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-4">
            Distribuição por Nível
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Object.entries(levelCounts).map(([level, count]) => (
              <div key={level} className="text-center">
                <div className={`p-3 rounded-monumenta ${getLevelColor(level)} border border-current`}>
                  <div className="font-semibold text-lg">{count}</div>
                  <div className="text-xs capitalize">{level}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Search and Filters */}
        <Card className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-gray pointer-events-none z-10" />
                <Input
                  placeholder="Buscar por título ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  style={{ paddingLeft: '3rem' }}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <option value="">Todos os departamentos</option>
                {departments.map(dept => (
                  <option key={dept.departmentId} value={dept.departmentId}>{dept.name}</option>
                ))}
              </Select>
              <Select
                value={selectedLevel}
                onValueChange={setSelectedLevel}
              >
                <option value="">Todos os níveis</option>
                <option value="junior">Junior</option>
                <option value="mid">Mid-level</option>
                <option value="senior">Senior</option>
                <option value="lead">Lead</option>
                <option value="manager">Manager</option>
                <option value="director">Director</option>
                <option value="executive">Executive</option>
              </Select>
            </div>
          </div>
        </Card>

        {/* 🚀 Background refresh indicator (subtle, non-blocking) */}
        {isFetching && !loading && (
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-md">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>Sincronizando...</span>
          </div>
        )}

        {/* Roles List */}
        {loading ? (
          <SkeletonTable rows={8} columns={4} />
        ) : filteredRoles.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="Nenhum cargo encontrado"
            description="Não há cargos que correspondam aos critérios de busca."
            action={
              <Link to="/workforce/roles/new">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Cargo
                </Button>
              </Link>
            }
          />
        ) : (
          <div className="grid gap-6">
            {filteredRoles.map((role) => (
              <Card key={role.roleId} className="card-hover p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Link to={`/workforce/roles/${role.roleId}` as any}>
                        <h3 className="font-display text-xl font-semibold text-white hover:text-brand-primary transition-colors">
                          {role.name}
                        </h3>
                      </Link>
                      {role.level && (
                        <Badge className={getLevelColor(role.level)}>
                          <div className="flex items-center gap-1">
                            {getLevelIcon(role.level)}
                            <span className="capitalize ml-1">{role.level}</span>
                          </div>
                        </Badge>
                      )}
                    </div>

                    {role.description && (
                      <p className="text-brand-gray mb-4 max-w-3xl">
                        {role.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="space-y-2">
                        <div className="text-sm text-brand-gray">Departamento</div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-brand-primary" />
                          <span className="font-semibold text-white">
                            {role.department?.name || 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm text-brand-gray">Colaboradores</div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-brand-primary" />
                          <span className="font-semibold text-white">
                            {role.employees?.length || 0}
                          </span>
                        </div>
                      </div>

                      {role.salary && (
                        <div className="space-y-2">
                          <div className="text-sm text-brand-gray">Faixa Salarial</div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-brand-primary" />
                            <span className="font-semibold text-white text-sm">
                              {formatCurrency(role.salary.min)} - {formatCurrency(role.salary.max)}
                            </span>
                          </div>
                        </div>
                      )}

                      {role.level && (
                        <div className="space-y-2">
                          <div className="text-sm text-brand-gray">Nível</div>
                          <div className="flex items-center gap-2">
                            <div className="flex">{getLevelIcon(role.level)}</div>
                            <span className="font-semibold text-white capitalize">
                              {role.level}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Responsibilities and Requirements */}
                    <div className="mt-6 pt-4 border-t border-brand-gray/20">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {role.responsibilities && role.responsibilities.length > 0 && (
                          <div>
                            <div className="text-sm text-brand-gray mb-2">Responsabilidades</div>
                            <div className="space-y-1">
                              {role.responsibilities.slice(0, 3).map((resp, index) => (
                                <div key={index} className="text-sm text-white flex items-start gap-2">
                                  <div className="w-1 h-1 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                                  <span>{resp}</span>
                                </div>
                              ))}
                              {role.responsibilities.length > 3 && (
                                <div className="text-sm text-brand-gray">
                                  +{role.responsibilities.length - 3} mais...
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {role.requirements && role.requirements.length > 0 && (
                          <div>
                            <div className="text-sm text-brand-gray mb-2">Requisitos</div>
                            <div className="space-y-1">
                              {role.requirements.slice(0, 3).map((req, index) => (
                                <div key={index} className="text-sm text-white flex items-start gap-2">
                                  <div className="w-1 h-1 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                                  <span>{req}</span>
                                </div>
                              ))}
                              {role.requirements.length > 3 && (
                                <div className="text-sm text-brand-gray">
                                  +{role.requirements.length - 3} mais...
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Link to={`/workforce/roles/${role.roleId}` as any}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </Link>
                    <Link to={`/workforce/roles/${role.roleId}/edit` as any}>
                      <Button variant="ghost" size="sm">
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    </Link>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
  )
}

// Apply RBAC protection - only admin can access workforce roles
export default withPageProtection(RolesContent, PAGES.WORKFORCE_ROLES)